import {SQFT_FEATURES} from "../constants";
import {is3DMode} from "../components/navigation/NavigationHelper";
import {
  hasRelatedCabinetModelForStyle,
} from "./sqft3dArHelper";
import {getCabinetSimilarFeatureFromAnotherColor} from "./cabinetFeatureMatcher";
import {getUrlMatchingByColor} from "./cabinetModelUrlMatcher";
import {getWizardOrFeatureGroupNavItems} from "./sqftNavigationHelper";
import {getCurrentLayout} from "../../appLayout";
import workspace from "../components/canvas/core/workspace";
import {getDbFeaturesByIdMap} from "../../../misc/featureStore";

const CABINET_STYLE_PART_IDS = [
  SQFT_FEATURES.top_cabinet,
  SQFT_FEATURES.wall_vanity_cabinet,
  SQFT_FEATURES.bottom_cabinet,
];

const CABINET_STYLE_RELATED_PART_IDS = {
  [SQFT_FEATURES.top_cabinet]: SQFT_FEATURES.upper_cabinet_boxes,
  [SQFT_FEATURES.wall_vanity_cabinet]: SQFT_FEATURES.wall_cabinet_boxes,
  [SQFT_FEATURES.bottom_cabinet]: SQFT_FEATURES.lower_cabinet_boxes,
};

const createDbFeatureByIdMap = () => {
  const layout = getCurrentLayout();
  // the layout already keeps an id index (read only here), no need to rebuild it on every call
  return layout.featureStore ? layout.featureStore.byId : getDbFeaturesByIdMap(layout.allDbFeatures);
};

const getRelatedDbFeatures = (parentFeature, dbFeatureById) => {
  const parentDbFeature = parentFeature.dbFeature;
  const loadedRelatedFeatures = Array.isArray(parentDbFeature.relatedFeatures)
    ? parentDbFeature.relatedFeatures
    : [];
  const relatedFeatureIds = Array.isArray(parentDbFeature.relatedFeatureIds)
    ? parentDbFeature.relatedFeatureIds
    : [];
  const relatedDbFeatureById = new Map(
    loadedRelatedFeatures.map(relatedDbFeature => [relatedDbFeature.id, relatedDbFeature])
  );

  relatedFeatureIds.forEach(relatedFeatureId => {
    const relatedDbFeature = dbFeatureById.get(relatedFeatureId);
    if (relatedDbFeature) {
      relatedDbFeatureById.set(relatedFeatureId, relatedDbFeature);
    }
  });

  return Array.from(relatedDbFeatureById.values());
};

const getSceneCabinetFeatures = (partId) => {
  return workspace.getShapes()
    .filter(shape => shape.parentPartId === partId || shape.grandParentPartId === partId)
    .map(shape => shape.getFeature && shape.getFeature())
    .filter(Boolean);
};

const hasModelsForSceneCabinets = (parentFeature, relatedDbFeatures, sceneCabinetFeatures) => {
  return sceneCabinetFeatures.every(sceneCabinetFeature => {
    const matchingRelatedDbFeature = getCabinetSimilarFeatureFromAnotherColor(
      relatedDbFeatures,
      sceneCabinetFeature.dbFeature
    );

    return matchingRelatedDbFeature
      && Boolean(getUrlMatchingByColor(matchingRelatedDbFeature, parentFeature));
  });
};

export const getCabinetStyleFeatureLoadPartIds = (partIds) => {
  if (!is3DMode()) {
    return partIds;
  }

  const relatedPartIds = partIds
    .map(partId => CABINET_STYLE_RELATED_PART_IDS[partId])
    .filter(Boolean);

  return Array.from(new Set([...partIds, ...relatedPartIds]));
};

// Resolve catalog and scene context once for each availability traversal.
const createCabinetStyleAvailabilityCheck = (partId) => {
  const dbFeatureById = createDbFeatureByIdMap();
  const sceneCabinetFeatures = getSceneCabinetFeatures(partId);

  return navItem => {
    const relatedDbFeatures = getRelatedDbFeatures(navItem.part, dbFeatureById);

    if (sceneCabinetFeatures.length) {
      return hasModelsForSceneCabinets(navItem.part, relatedDbFeatures, sceneCabinetFeatures);
    }

    return hasRelatedCabinetModelForStyle(navItem.part, relatedDbFeatures);
  };
};

export const getDisplayedCabinetStyleNavItems = (partId, featureData) => {
  const navItems = getWizardOrFeatureGroupNavItems(partId, featureData);
  if (!navItems.length || !is3DMode() || !CABINET_STYLE_PART_IDS.includes(partId)) {
    return navItems;
  }

  return navItems.filter(createCabinetStyleAvailabilityCheck(partId));
};

// Menu buttons need existence, while the style picker needs the complete list.
export const hasDisplayedCabinetStyleNavItems = (partId, featureData) => {
  const navItems = getWizardOrFeatureGroupNavItems(partId, featureData);
  if (!navItems.length) {
    return false;
  }
  if (!is3DMode() || !CABINET_STYLE_PART_IDS.includes(partId)) {
    return true;
  }

  return navItems.some(createCabinetStyleAvailabilityCheck(partId));
};
